import React, { useEffect, useState } from 'react';
import { Badge, Card, Col, Container, Nav, Row } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BASE_URL = process.env.REACT_APP_API_URL;

const JBranchReAuditChecklist = () => {
  const [reauditChecklists, setReauditChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState({});
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'all');
  const navigate = useNavigate();

  const auditorData = JSON.parse(localStorage.getItem("auditor"));
  const auditorId = auditorData?.id;
  const BranchCode = auditorData?.BranchCode;

  const notify = (message, type = 'success') => toast[type](message, {
    position: "top-right",
    autoClose: 3000
  });

  useEffect(() => {
    const fetchReauditChecklists = async () => {
      if (!auditorId || !BranchCode) {
        notify("Auditor ID or Branch Code is missing", "error");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${BASE_URL}/get-reaudit-checklists/${auditorId}/${BranchCode}`
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch re-audit checklists: ${response.status}`);
        }

        const data = await response.json();
        setReauditChecklists(Array.isArray(data) ? data : []);

      } catch (error) {
        console.error("Error:", error);
        notify("Failed to load re-audit checklists", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchReauditChecklists();
  }, [auditorId, BranchCode]);

  useEffect(() => {
    const fetchAllProgress = async () => {
      for (const checklist of reauditChecklists) {
        await fetchProgress(checklist);
      }
    };
    
    if (reauditChecklists.length > 0) {
      fetchAllProgress();
    }
  }, [reauditChecklists]);

  const fetchProgress = async (checklist) => {
    try {
      const response = await fetch(
        `${BASE_URL}/get-reaudit-progress/${checklist.original_checklist_id}/${checklist.id}/${auditorId}`
      );
      
      if (!response.ok) throw new Error('Failed to fetch progress');
      
      const data = await response.json();
      setProgressData(prevState => ({
        ...prevState,
        [checklist.id]: data
      }));
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'success';
      case 'in progress':
        return 'warning';
      case 'pending':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const handleStartReaudit = (checklist) => {
    navigate('/JBranchReAuditDetails', {
      state: { 
        checklist: {
          id: checklist.id,
          original_checklist_id: checklist.original_checklist_id,
          assigned_checklist_id: checklist.assigned_checklist_id,
          checklist_name: checklist.checklist_name,
          branch_id: checklist.branch_id,
          BranchCode: checklist.BranchCode,
          branches: checklist.branches,
          status: checklist.status
        }
      }
    });
  };

  const CircularProgress = ({ percentage, size = 60 }) => {
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (percentage / 100) * circumference;

    // Get color based on percentage
    const getProgressColor = (percent) => {
      if (percent >= 100) return '#198754'; // success/green
      if (percent >= 75) return '#0d6efd';  // primary/blue
      if (percent >= 50) return '#ffc107';  // warning/yellow
      if (percent >= 25) return '#fd7e14';  // orange
      return '#dc3545';                      // danger/red
    };

    const progressColor = getProgressColor(percentage);

    return (
      <div className="circular-progress" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle
            className="progress-bg"
            stroke="#e9ecef"
            strokeWidth={strokeWidth}
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <circle
            className="progress-bar"
            stroke={progressColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            fill="none"
            r={radius}
            cx={size / 2}
            cy={size / 2}
          />
          <text
            x="50%"
            y="50%"
            textAnchor="middle"
            dy=".1em"
            dominantBaseline="middle"
            className="progress-text"
            style={{ fill: progressColor }}
          >
            {percentage}%
          </text>
        </svg>
      </div>
    );
  };

  const filterChecklistsByStatus = (status) => {
    if (status === 'all') return reauditChecklists;
    return reauditChecklists.filter(checklist => 
      checklist.status.toLowerCase() === status.toLowerCase()
    );
  };

  const getTabCount = (status) => {
    if (status === 'all') return reauditChecklists.length;
    return reauditChecklists.filter(checklist => 
      checklist.status.toLowerCase() === status.toLowerCase()
    ).length;
  };

  if (loading) {
    return (
      <Container fluid className="py-5">
        <div className="text-center py-5">
          <div className="spinner-grow text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-primary fw-bold mb-1">Loading Re-Audit Checklists</p>
          <p className="text-muted">Please wait while we fetch your re-audit data...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-3 py-md-4" style={{ backgroundColor: '#f8f9fa' }}>
      <ToastContainer />
      
      <div className="mb-3 mb-md-4">
        <h2 className="text-primary h4 h2-md mb-2">My Re-Audit Checklists</h2>
        <p className="text-muted small mb-0">Review and complete re-audit checklists for CAP items</p>
      </div>

      {/* Tabs for filtering */}
      <div className="mb-4">
        <Nav variant="tabs" activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav.Item>
            <Nav.Link eventKey="all" className="d-flex align-items-center gap-2">
              All
              <Badge bg="secondary" pill>{getTabCount('all')}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="pending" className="d-flex align-items-center gap-2">
              Pending
              <Badge bg="danger" pill>{getTabCount('pending')}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="in progress" className="d-flex align-items-center gap-2">
              In Progress
              <Badge bg="warning" pill>{getTabCount('in progress')}</Badge>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="completed" className="d-flex align-items-center gap-2">
              Completed
              <Badge bg="success" pill>{getTabCount('completed')}</Badge>
            </Nav.Link>
          </Nav.Item>
        </Nav>
      </div>
      
      <Row xs={1} md={2} lg={3} className="g-3 g-md-4 justify-content-start card-grid">
        {filterChecklistsByStatus(activeTab).map((checklist) => (
          <Col key={checklist.id} className="card-column">
            <Card className="h-100 shadow-sm hover-shadow card-audit" 
                  style={{ transition: 'all 0.3s ease-in-out' }}>
              <Card.Header className="bg-white border-bottom-0 p-3">
                <div className="d-flex justify-content-between align-items-start gap-2">
                  <div className="flex-grow-1">
                    <h5 className="text-primary h6 mb-2 text-truncate">
                      {checklist.branches}
                    </h5>
                    <div className="text-muted small text-truncate">
                      {checklist.checklist_name}
                    </div>
                  </div>
                  <Badge 
                    bg={getStatusBadgeColor(checklist.status)}
                    className="px-2 py-1 rounded-pill status-badge"
                  >
                    {checklist.status}
                  </Badge>
                </div>
              </Card.Header>
              
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center mb-3 gap-3">
                  <CircularProgress 
                    percentage={progressData[checklist.id]?.progressPercentage || 0}
                    size={50}
                  />
                  <div className="progress-stats flex-grow-1">
                    <div className="stat-item d-flex justify-content-between align-items-center">
                      <small className="text-muted">Completed:</small>
                      <span className="fw-medium">
                        {progressData[checklist.id]?.answeredQuestions || 0}
                      </span>
                    </div>
                    <div className="stat-item d-flex justify-content-between align-items-center">
                      <small className="text-muted">Total CAP Items:</small>
                      <span>
                        {progressData[checklist.id]?.totalQuestions || 0}
                      </span>
                    </div>
                  </div>
                </div>
                <button 
                  className="btn btn-primary w-100 rounded-3"
                  onClick={() => handleStartReaudit(checklist)}
                >
                  <i className="fas fa-clipboard-check me-2"></i>
                  {checklist.status.toLowerCase() === 'completed' ? 'View Details' : 'Start Re-Audit'}
                </button>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {filterChecklistsByStatus(activeTab).length === 0 && (
        <div className="text-center py-5">
          <div className="mb-3">
            <i className="fas fa-clipboard-list fa-3x text-muted"></i>
          </div>
          <h4 className="h5 text-muted mb-2">
            {activeTab === 'all' ? 'No re-audit checklists available' : `No ${activeTab} re-audit checklists`}
          </h4>
          <p className="text-muted small mb-0">
            {activeTab === 'all' 
              ? 'Re-audit checklists will appear here when CAP actions are assigned.'
              : `No re-audit checklists with ${activeTab} status found.`
            }
          </p>
        </div>
      )}

      <style>
        {`
          .hover-shadow {
            transition: all 0.3s ease-in-out;
          }
          
          .hover-shadow:hover {
            transform: translateY(-2px);
            box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
          }
          
          .circular-progress {
            position: relative;
          }
          
          .progress-bg {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
          }
          
          .progress-bar {
            transform: rotate(-90deg);
            transform-origin: 50% 50%;
            transition: stroke-dashoffset 0.35s, stroke 0.35s;
          }
          
          .progress-text {
            font-size: 12px;
            font-weight: 600;
            transition: fill 0.35s;
          }
          
          .progress-stats {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .stat-item {
            font-size: 0.875rem;
          }

          .status-badge {
            font-size: 0.7rem;
            white-space: nowrap;
            flex-shrink: 0;
            max-width: 90px;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          
          .card-header .d-flex {
            min-height: 50px;
          }
          
          .card-header .flex-grow-1 {
            max-width: calc(100% - 95px);
          }

          @media (min-width: 768px) {
            .h2-md {
              font-size: 2rem;
            }
            
            .progress-text {
              font-size: 14px;
            }
          }

          @media (max-width: 576px) {
            .card {
              margin: 0 0.5rem;
            }
            
            .container-fluid {
              padding-left: 1rem;
              padding-right: 1rem;
            }
          }

          .card-grid {
            display: flex;
            flex-wrap: wrap;
          }

          .card-column {
            display: flex;
            padding: 0.75rem;
          }

          .card-audit {
            width: 100%;
            max-width: 400px;
            margin: 0 auto;
            border-radius: 8px;
            overflow: hidden;
          }

          @media (max-width: 768px) {
            .card-column {
              padding: 0.5rem;
            }
          }

          @media (min-width: 992px) {
            .card-grid {
              justify-content: flex-start;
            }
            
            .card-grid:not(.row-fill) {
              justify-content: center;
            }
          }

          .spinner-grow {
            animation-duration: 1.5s;
          }

          .text-primary.fw-bold {
            font-size: 1.2rem;
          }

          @keyframes fadeOut {
            from { opacity: 1; }
            to { opacity: 0; }
          }

          .loading-fadeout {
            animation: fadeOut 0.5s ease-out forwards;
          }

          .nav-tabs {
            border-bottom: 2px solid #dee2e6;
          }
          
          .nav-tabs .nav-link {
            color: #6c757d;
            border: none;
            border-bottom: 2px solid transparent;
            padding: 0.75rem 1rem;
            font-weight: 500;
            transition: all 0.3s ease;
          }
          
          .nav-tabs .nav-link:hover {
            border-bottom-color: #0d6efd;
            color: #0d6efd;
          }
          
          .nav-tabs .nav-link.active {
            color: #0d6efd;
            background-color: transparent;
            border-bottom-color: #0d6efd;
          }
          
          .nav-tabs .nav-link .badge {
            font-size: 0.7rem;
            margin-left: 0.25rem;
          }
        `}
      </style>
    </Container>
  );
};

export default JBranchReAuditChecklist;